import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Building2, Check, Scale, Settings as SettingsIcon, X } from 'lucide-react';
import { SETTING_UNITS, settingLabel } from '@unifemga/shared';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  Button,
  Card,
  CardContent,
  Input,
  PageHeader,
  Skeleton,
  Switch,
} from '@/components/ui';

interface Setting {
  key: string;
  value: unknown;
  category: string;
  description: string | null;
  isPublic: boolean;
  readOnly: boolean;
  updatedAt: string;
}

const CATEGORY_META: Record<string, { label: string; icon: typeof Building2; blurb: string }> = {
  organisation: {
    label: 'Organisation',
    icon: Building2,
    blurb: 'Name and contact details shown on the public site and in every message.',
  },
  lending: {
    label: 'Lending',
    icon: Scale,
    blurb: 'Thresholds that govern arrears and access to further credit.',
  },
  legal: {
    label: 'Legal',
    icon: SettingsIcon,
    blurb: 'Versions of the terms and privacy policy members are recorded as having accepted.',
  },
  general: { label: 'General', icon: SettingsIcon, blurb: '' },
};

export default function AdminSettings() {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<Setting[]>('/settings'),
  });

  const save = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) =>
      api.patch(`/settings/${key}`, { value }),
    onSuccess: (_, variables) => {
      toast.success(`Saved ${variables.key}.`);
      setDrafts((current) => {
        const next = { ...current };
        delete next[variables.key];
        return next;
      });
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      void queryClient.invalidateQueries({ queryKey: ['public-settings'] });
    },
    onError: (error) =>
      toast.error(error instanceof ApiError ? error.message : 'Could not save that setting.'),
  });

  if (isLoading) return <Skeleton className="h-96" />;

  // The Qard Hasan keys are declarations, not settings: the API refuses to
  // change them and no operator decision hangs on them. They stay in the API as
  // the machine-readable statement the public site reads; they do not belong on
  // a screen whose entire purpose is changing things.
  const byCategory = settings
    .filter((setting) => !setting.readOnly)
    .reduce<Record<string, Setting[]>>((acc, setting) => {
    (acc[setting.category] ??= []).push(setting);
    return acc;
    }, {});

  const ORDER = ['lending', 'organisation', 'legal'];
  const categories = Object.entries(byCategory).sort(
    ([a], [b]) =>
      (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b),
  );

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Settings"
        description="Values an administrator can change. Every change is written to the audit trail."
      />

      <div className="grid gap-6 xl:grid-cols-2">
        {categories.map(([category, items]) => {
          const meta = CATEGORY_META[category] ?? CATEGORY_META['general']!;
          const Icon = meta.icon;

          return (
            <Card key={category}>
              <CardContent className="p-6">
                <div className="mb-5 flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground-secondary">
                    <Icon size={16} />
                  </span>
                  <div>
                    <h2 className="font-heading font-bold text-foreground">{meta.label}</h2>
                    {meta.blurb && (
                      <p className="mt-0.5 text-sm leading-relaxed text-foreground-secondary">
                        {meta.blurb}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {items.map((setting) => (
                    <SettingRow
                      key={setting.key}
                      setting={setting}
                      draft={drafts[setting.key]}
                      onDraft={(value) =>
                        setDrafts((current) => ({ ...current, [setting.key]: value }))
                      }
                      onSave={(value) => save.mutate({ key: setting.key, value })}
                      saving={save.isPending && save.variables?.key === setting.key}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function SettingRow({
  setting,
  draft,
  onDraft,
  onSave,
  saving,
}: {
  setting: Setting;
  draft: string | undefined;
  onDraft: (value: string) => void;
  onSave: (value: unknown) => void;
  saving: boolean;
}) {
  const isBoolean = typeof setting.value === 'boolean';
  const isNumber = typeof setting.value === 'number';
  const current = String(setting.value ?? '');
  const dirty = draft !== undefined && draft !== current;

  return (
    <div
      className="rounded-xl border border-border p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-foreground">{settingLabel(setting.key)}</span>
            {setting.isPublic && (
              <span className="rounded-full bg-info-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-info-soft-foreground">
                Public
              </span>
            )}
          </div>
          {setting.description && (
            <p className="mt-1 text-sm leading-relaxed text-foreground-secondary">
              {setting.description}
            </p>
          )}
        </div>

        {/* Booleans get a switch; everything else an input. */}
        {isBoolean ? (
          <div className="flex shrink-0 items-center gap-2">
            <span
              className={cn(
                'text-sm font-semibold',
                setting.value ? 'text-success' : 'text-foreground-secondary',
              )}
            >
              {setting.value ? 'Yes' : 'No'}
            </span>
            <Switch
              checked={Boolean(setting.value)}
              disabled={saving}
              label={settingLabel(setting.key)}
              onChange={(value) => onSave(value)}
            />
          </div>
        ) : (
          <div className="flex w-full shrink-0 items-center gap-2 sm:w-80">
            <div className="relative flex-1">
              <Input
                value={draft ?? current}
                disabled={saving}
                onChange={(e) => onDraft(e.target.value)}
                className={cn(isNumber && 'numeric', SETTING_UNITS[setting.key] && 'pr-16')}
              />
              {SETTING_UNITS[setting.key] && (
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-foreground-secondary">
                  {SETTING_UNITS[setting.key]}
                </span>
              )}
            </div>
            {dirty && (
              <>
                <Button
                  size="icon"
                  loading={saving}
                  onClick={() => onSave(isNumber ? Number(draft) : draft)}
                  aria-label="Save"
                >
                  <Check size={15} />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onDraft(current)}
                  aria-label="Cancel"
                >
                  <X size={15} />
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
